import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, apiErrorDetail } from "@/lib/api";
import { PageHeader, EmptyState } from "./ui";
import { EMPLOYEE_COLORS, WEEKDAYS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserCog, Plus, Check, Power, Loader2, Info, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", color: EMPLOYEE_COLORS[0], active: true, login_password: "", working_hours: {}, breaks: [], service_ids: [] };

export default function Employees() {
    const { t } = useI18n();
    const { salon } = useAuth();
    const qc = useQueryClient();
    const [openD, setOpenD] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);

    const { data: employees, isLoading } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });

    const plan = salon?.subscription?.plan;
    const isTrial = salon?.subscription?.status === "trial";
    const limited = !isTrial && plan === "basic";

    const openNew = () => { setEditing(null); setForm({ ...empty, color: EMPLOYEE_COLORS[(employees?.length || 0) % EMPLOYEE_COLORS.length], working_hours: salon?.opening_hours || {}, breaks: [] }); setOpenD(true); };
    const openEdit = (e) => { setEditing(e); setForm({ ...empty, ...e, email: e.email || "", phone: e.phone || "", login_password: "", working_hours: e.working_hours || {}, breaks: e.breaks || [], service_ids: e.service_ids || [] }); setOpenD(true); };
    const { data: allServices } = useQuery({ queryKey: ["services"], queryFn: async () => (await api.get("/services")).data });

    const setHour = (i, f, v) => setForm((fm) => ({ ...fm, working_hours: { ...fm.working_hours, [String(i)]: { ...(fm.working_hours[String(i)] || { open: "09:00", close: "18:00", closed: false }), [f]: v } } }));
    const addBreak = () => setForm((fm) => ({ ...fm, breaks: [...(fm.breaks || []), { day: "0", start: "12:00", end: "12:30" }] }));
    const setBreak = (idx, f, v) => setForm((fm) => ({ ...fm, breaks: fm.breaks.map((b, j) => (j === idx ? { ...b, [f]: v } : b)) }));
    const removeBreak = (idx) => setForm((fm) => ({ ...fm, breaks: fm.breaks.filter((_, j) => j !== idx) }));

    const applyToAll = async () => {
        if (!editing) return;
        setSaving(true);
        try {
            await api.patch(`/employees/${editing.id}`, { name: form.name, working_hours: form.working_hours, breaks: form.breaks });
            const { data } = await api.post(`/employees/${editing.id}/copy-schedule`);
            qc.invalidateQueries({ queryKey: ["employees"] });
            setOpenD(false);
            toast.success(`${t("employees.applied")} ${data.updated} ${t("employees.title").toLowerCase()}`);
        } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
    };

    const save = async () => {
        setSaving(true);
        try {
            if (editing) await api.patch(`/employees/${editing.id}`, form);
            else await api.post("/employees", form);
            qc.invalidateQueries({ queryKey: ["employees"] });
            setOpenD(false); toast.success(t("common.saved"));
        } catch (e) {
            const d = apiErrorDetail(e);
            if (d?.code === "employee_limit_reached") toast.error(t("employees.limitInfo"));
            else toast.error(formatApiError(e));
        } finally { setSaving(false); }
    };

    const toggleActive = async (emp) => {
        try {
            await api.patch(`/employees/${emp.id}`, { name: emp.name, active: !emp.active });
            qc.invalidateQueries({ queryKey: ["employees"] });
        } catch (e) {
            const d = apiErrorDetail(e);
            if (d?.code === "employee_limit_reached") toast.error(t("employees.limitInfo"));
            else toast.error(formatApiError(e));
        }
    };

    const activeCount = employees?.filter((e) => e.active).length || 0;

    return (
        <div>
            <PageHeader title={t("employees.title")}
                subtitle={limited ? `${activeCount} / 2 ${t("common.active").toLowerCase()}` : (isTrial || plan === "professional" ? t("marketing.unlimitedEmployees") : null)}
                action={<Button onClick={openNew} className="rounded-full" data-testid="new-employee-btn"><Plus className="mr-1 h-4 w-4" />{t("employees.newEmployee")}</Button>} />

            {limited && (
                <div className="mb-5 flex items-start gap-2 rounded-xl border border-accent/40 bg-accent/10 p-3 text-sm" data-testid="employee-limit-info">
                    <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" /><span>{t("employees.limitInfo")}</span>
                </div>
            )}

            {isLoading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-accent" /></div>
                : employees.length === 0 ? <EmptyState icon={UserCog} title={t("empty.employees")} action={<Button onClick={openNew} className="rounded-full"><Plus className="mr-1 h-4 w-4" />{t("employees.newEmployee")}</Button>} />
                    : (
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {employees.map((e) => (
                                <div key={e.id} data-testid={`employee-${e.id}`} className={`rounded-2xl border border-border/70 bg-card p-5 ${!e.active ? "opacity-60" : ""}`}>
                                    <div className="flex items-center gap-3">
                                        <span className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-white" style={{ background: e.color }}>{e.name.slice(0, 2).toUpperCase()}</span>
                                        <div className="min-w-0 flex-1"><p className="truncate font-semibold">{e.name}</p><p className="truncate text-xs text-muted-foreground">{e.email || e.phone || "—"}</p></div>
                                    </div>
                                    <div className="mt-4 flex items-center gap-2">
                                        <Button variant="outline" size="sm" onClick={() => openEdit(e)} className="flex-1 rounded-full" data-testid={`edit-employee-${e.id}`}>{t("common.edit")}</Button>
                                        <Button variant="ghost" size="sm" onClick={() => toggleActive(e)} className="rounded-full" data-testid={`toggle-employee-${e.id}`}>
                                            <Power className="mr-1 h-3.5 w-3.5" />{e.active ? t("employees.deactivate") : t("employees.activate")}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

            <Dialog open={openD} onOpenChange={setOpenD}>
                <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
                    <DialogHeader><DialogTitle>{editing ? t("common.edit") : t("employees.newEmployee")}</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                        <div><Label>{t("common.name")}</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name" className="mt-1.5" /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>{t("common.email")}</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
                            <div><Label>{t("common.phone")}</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
                        </div>
                        <div>
                            <Label>{t("employees.color")}</Label>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {EMPLOYEE_COLORS.map((c) => (
                                    <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} data-testid={`emp-color-${c}`}
                                        className={`flex h-8 w-8 items-center justify-center rounded-full transition-transform duration-200 hover:scale-110 ${form.color === c ? "ring-2 ring-offset-2 ring-foreground" : ""}`} style={{ background: c }}>
                                        {form.color === c && <Check className="h-4 w-4 text-white" />}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {!editing && (
                            <div><Label>{t("employees.loginPassword")} ({t("common.optional")})</Label><Input type="password" value={form.login_password} onChange={(e) => setForm({ ...form, login_password: e.target.value })} className="mt-1.5" placeholder={t("employees.createLogin")} /></div>
                        )}
                        <div>
                            <Label>{t("employees.workingHours")} & {t("employees.daysOff")}</Label>
                            <div className="mt-2 max-h-52 space-y-1.5 overflow-y-auto rounded-xl border border-border/70 p-3">
                                {WEEKDAYS.map((d, i) => {
                                    const h = form.working_hours[String(i)] || { open: "09:00", close: "18:00", closed: false };
                                    return (
                                        <div key={i} className="flex items-center gap-2" data-testid={`emp-hours-${i}`}>
                                            <span className="w-10 text-xs font-medium">{d}</span>
                                            <Switch checked={!h.closed} onCheckedChange={(v) => setHour(i, "closed", !v)} data-testid={`emp-hours-toggle-${i}`} />
                                            {h.closed ? <span className="text-xs text-muted-foreground">{t("settings.closed")}</span> : (
                                                <>
                                                    <Input type="time" value={h.open} onChange={(e) => setHour(i, "open", e.target.value)} className="h-8 w-28" />
                                                    <span className="text-muted-foreground">–</span>
                                                    <Input type="time" value={h.close} onChange={(e) => setHour(i, "close", e.target.value)} className="h-8 w-28" />
                                                </>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between">
                                <Label>{t("employees.breaks")}</Label>
                                <Button variant="outline" size="sm" onClick={addBreak} className="h-7 rounded-full" data-testid="emp-add-break"><Plus className="mr-1 h-3 w-3" />{t("common.add")}</Button>
                            </div>
                            <div className="mt-2 space-y-2">
                                {(form.breaks || []).length === 0 && <p className="text-xs text-muted-foreground">—</p>}
                                {(form.breaks || []).map((b, idx) => (
                                    <div key={idx} className="flex items-center gap-2" data-testid={`emp-break-${idx}`}>
                                        <Select value={String(b.day)} onValueChange={(v) => setBreak(idx, "day", v)}>
                                            <SelectTrigger className="h-8 w-28"><SelectValue /></SelectTrigger>
                                            <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                                        </Select>
                                        <Input type="time" value={b.start} onChange={(e) => setBreak(idx, "start", e.target.value)} className="h-8 w-24" />
                                        <span className="text-muted-foreground">–</span>
                                        <Input type="time" value={b.end} onChange={(e) => setBreak(idx, "end", e.target.value)} className="h-8 w-24" />
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeBreak(idx)}><Trash2 className="h-4 w-4" /></Button>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center justify-between"><Label>{t("common.active")}</Label><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /></div>
                        <div data-testid="employee-skills">
                            <Label>{t("sched.performsServices")}</Label>
                            <p className="mb-2 text-xs text-muted-foreground">{t("sched.allServices")}</p>
                            <div className="flex flex-wrap gap-2">
                                {(allServices || []).map((s) => {
                                    const on = (form.service_ids || []).includes(s.id);
                                    return <button key={s.id} type="button" data-testid={`skill-${s.id}`} onClick={() => setForm((f) => ({ ...f, service_ids: on ? f.service_ids.filter((x) => x !== s.id) : [...(f.service_ids || []), s.id] }))}
                                        className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-200 ${on ? "border-primary bg-primary text-primary-foreground" : "border-border/70 text-muted-foreground hover:text-foreground"}`}>{s.name}</button>;
                                })}
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        {editing && <Button variant="ghost" onClick={applyToAll} disabled={saving} className="mr-auto rounded-full" data-testid="copy-schedule"><Info className="mr-1 h-4 w-4" />{t("employees.applyToAll")}</Button>}
                        <Button variant="outline" onClick={() => setOpenD(false)} className="rounded-full">{t("common.cancel")}</Button>
                        <Button onClick={save} disabled={saving || !form.name} className="rounded-full" data-testid="save-employee">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
